import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { toast } from 'sonner'
import * as performanceAPI from '@/api/performance'
import { evaluationKeys, analyticsKeys } from './feedbackKeys'
import type {
  Evaluation,
  EvaluationsListResponse,
  CreateEvaluationData,
  UpdateEvaluationData,
  EvaluationTemplate,
  PerformanceByTheme,
} from '@/types/performance'

// ================================
// QUERIES
// ================================

/**
 * Use My Evaluations Query
 *
 * Fetches evaluations for the current user.
 * Cached for 1 minute.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useMyEvaluations({ status: 'submitted' })
 * // data = { items: [...], pagination: {...} }
 * ```
 */
export function useMyEvaluations(params?: {
  status?: 'draft' | 'submitted' | 'reviewed' | 'completed'
  limit?: number
  nextToken?: string
}) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: evaluationKeys.my(tenantDomain!, params),
    queryFn: async (): Promise<EvaluationsListResponse> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getMyEvaluations(tenantDomain, {
        status: params?.status,
        limit: params?.limit || 25,
        nextToken: params?.nextToken,
      })
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use My Created Evaluations Query
 *
 * Fetches evaluations created/performed BY the current user (as evaluator).
 * Shows evaluations the user made of OTHER people (queries GSI3 by evaluatorId).
 */
export function useMyCreatedEvaluations(params?: {
  status?: 'draft' | 'submitted' | 'reviewed' | 'completed'
  limit?: number
  nextToken?: string
}) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: evaluationKeys.createdByMe(tenantDomain!, params),
    queryFn: async (): Promise<EvaluationsListResponse> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getMyCreatedEvaluations(tenantDomain, {
        status: params?.status,
        limit: params?.limit || 25,
        nextToken: params?.nextToken,
      })
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use Employee Evaluations Query
 *
 * Fetches evaluations for a specific employee (manager view).
 * Cached for 1 minute.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useEmployeeEvaluations('employee-123', { status: 'submitted' })
 * ```
 */
export function useEmployeeEvaluations(
  employeeId: string | null,
  params?: {
    status?: 'draft' | 'submitted' | 'reviewed' | 'completed'
    limit?: number
    nextToken?: string
  }
) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: evaluationKeys.employee(tenantDomain!, employeeId!, params),
    queryFn: async (): Promise<EvaluationsListResponse> => {
      if (!tenantDomain || !employeeId) throw new Error('Tenant domain and employee ID required')
      return await performanceAPI.getEmployeeEvaluations(tenantDomain, employeeId, {
        status: params?.status,
        limit: params?.limit || 25,
        nextToken: params?.nextToken,
      })
    },
    enabled: !!tenantDomain && !!employeeId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use My Evaluation Template Query
 *
 * Fetches the evaluation template for the current user's role.
 * Cached for 10 minutes - templates change rarely.
 *
 * @example
 * ```tsx
 * const { data: template, isLoading } = useMyEvaluationTemplate()
 * ```
 */
export function useMyEvaluationTemplate() {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: evaluationKeys.myTemplate(tenantDomain!),
    queryFn: async () => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      const template = await performanceAPI.getMyEvaluationTemplate(tenantDomain)
      return template as EvaluationTemplate | null
    },
    enabled: !!tenantDomain,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,
  })
}

/**
 * Use Performance By Theme Query
 *
 * Fetches performance analytics grouped by evaluation theme/category.
 * Returns actual per-theme averages calculated from individual scores.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePerformanceByTheme()
 * // data = [{ theme: 'Técnica', averageScore: 4.2, distribution: [...] }, ...]
 * ```
 */
export function usePerformanceByTheme() {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: analyticsKeys.performanceByTheme(tenantDomain!),
    queryFn: async (): Promise<PerformanceByTheme[]> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getPerformanceByTheme(tenantDomain)
    },
    enabled: !!tenantDomain,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000,
  })
}

// ================================
// MUTATIONS
// ================================

/**
 * Use Create Evaluation Mutation
 *
 * Creates a new evaluation.
 * Invalidates evaluations list on success.
 *
 * @example
 * ```tsx
 * const createEvaluation = useCreateEvaluation()
 *
 * await createEvaluation.mutateAsync({
 *   evaluatedUserId: 'user-123',
 *   templateId: 'template-456',
 *   period: '2024-Q1'
 * })
 * ```
 */
export function useCreateEvaluation() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateEvaluationData) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.createEvaluation(tenantDomain, data)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('Avaliação criada com sucesso!')

      // Invalidate evaluations list
      queryClient.invalidateQueries({
        queryKey: evaluationKeys.all,
      })
    },

    onError: (err) => {
      console.error('[CreateEvaluation] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao criar avaliação'
      )
    },
  })
}

/**
 * Use Submit Evaluation Mutation
 *
 * Submits a draft evaluation.
 * Invalidates evaluations list on success.
 *
 * @example
 * ```tsx
 * const submitEvaluation = useSubmitEvaluation()
 *
 * await submitEvaluation.mutateAsync('evaluation-123')
 * ```
 */
export function useSubmitEvaluation() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (evaluationId: string) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.submitEvaluation(tenantDomain, evaluationId)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('Avaliação registrada com sucesso!')

      // Invalidate all evaluations to refresh status
      queryClient.invalidateQueries({
        queryKey: evaluationKeys.all,
      })
    },

    onError: (err) => {
      console.error('[SubmitEvaluation] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao registrar avaliação'
      )
    },
  })
}

/**
 * Use Save Draft Evaluation Mutation
 *
 * Saves evaluation as draft (auto-save).
 * Does NOT show toast - silent operation.
 *
 * @example
 * ```tsx
 * const saveDraft = useSaveDraftEvaluation()
 *
 * await saveDraft.mutateAsync({
 *   evaluationId: 'eval-123',
 *   scores: [...]
 * })
 * ```
 */
export function useSaveDraftEvaluation() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateEvaluationData) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.saveDraftEvaluation(tenantDomain, data)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      // Silent success - just invalidate cache
      queryClient.invalidateQueries({
        queryKey: evaluationKeys.all,
      })
    },

    onError: (err) => {
      console.error('[SaveDraftEvaluation] Error:', err)
      // Silent error - don't show toast for auto-save failures
    },
  })
}

/**
 * Use All Evaluations Query (Admin)
 *
 * Fetches ALL evaluations for the tenant. Admin view.
 */
export function useAllEvaluations(params?: {
  status?: 'draft' | 'submitted' | 'reviewed' | 'completed'
  limit?: number
  nextToken?: string
}, enabled = false) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: [...evaluationKeys.all, 'all-tenant', tenantDomain, params] as const,
    queryFn: async (): Promise<EvaluationsListResponse> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getAllEvaluations(tenantDomain, {
        status: params?.status,
        limit: params?.limit || 50,
        nextToken: params?.nextToken,
      })
    },
    enabled: !!tenantDomain && enabled,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use All Individual Evaluations Query (Admin/VP/HR)
 *
 * Fetches ALL individual evaluations for the tenant. Admin/VP/HR view.
 */
export function useAllIndividualEvaluations(params?: {
  status?: string
  department?: string
  search?: string
  limit?: number
}, enabled = true) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: [...evaluationKeys.all, 'individual-all', tenantDomain, params] as const,
    queryFn: async () => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getAllIndividualEvaluations(tenantDomain, { ...params })
    },
    enabled: !!tenantDomain && enabled,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Use Delete Evaluation Mutation
 *
 * Deletes an evaluation and all its scores.
 * Invalidates evaluations list on success.
 */
export function useDeleteEvaluation() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (evaluationId: string) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.deleteEvaluation(tenantDomain, evaluationId)
    },

    onSuccess: () => {
      if (!tenantDomain) return

      toast.success('Avaliação excluída com sucesso!')

      queryClient.invalidateQueries({
        queryKey: evaluationKeys.all,
      })
    },

    onError: (err) => {
      console.error('[DeleteEvaluation] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao excluir avaliação'
      )
    },
  })
}

// ================================
// UTILITY HOOKS
// ================================

/**
 * Invalidate Evaluation Cache
 *
 * Manually invalidate evaluation cache to force refetch.
 * Useful for manual refresh buttons.
 *
 * @example
 * ```tsx
 * const invalidateEvaluations = useInvalidateEvaluationCache()
 *
 * <Button onClick={() => invalidateEvaluations()}>
 *   Refresh
 * </Button>
 * ```
 */
export function useInvalidateEvaluationCache() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return () => {
    if (!tenantDomain) return

    queryClient.invalidateQueries({
      queryKey: evaluationKeys.all,
    })
  }
}
